import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { type OpenSession } from '../services/openScheduleService';
import openScheduleService from '../services/openScheduleService';

interface SessionManagerProps {
  open: boolean;
  onClose: () => void;
  session: OpenSession | null;
  scheduleId: string;
  agencySlug: string;
  onSessionUpdate: (updatedSession: OpenSession) => void;
}

interface AttendeeForm {
  name: string;
  email: string;
  phone: string;
}

const SessionManager: React.FC<SessionManagerProps> = ({
  open,
  onClose,
  session,
  scheduleId,
  agencySlug,
  onSessionUpdate
}) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Keep track of original session duration in minutes so we can maintain it when start time changes
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState<number | null>(null);
  
  // Session edit form
  const [sessionForm, setSessionForm] = useState({
    startTime: session?.startTime || '',
    endTime: session?.endTime || '',
    notes: session?.notes || '',
    maxAttendees: session?.maxAttendees?.toString() || '',
    status: session?.status || 'scheduled' as const
  });

  // Attendee registration form
  const [attendeeForm, setAttendeeForm] = useState<AttendeeForm>({
    name: '',
    email: '',
    phone: ''
  });

  // Phone lookup state
  const [phoneLookupResults, setPhoneLookupResults] = useState<Array<{
    name: string;
    email?: string;
    phone: string;
  }>>([]);
  const [phoneLookupLoading, setPhoneLookupLoading] = useState(false);
  const [phoneLookupError, setPhoneLookupError] = useState<string | null>(null);
  const [hasAttemptedPhoneLookup, setHasAttemptedPhoneLookup] = useState(false);

  // Normalize phone number (remove spaces, dashes, parentheses, plus signs)
  const normalizePhoneNumber = useCallback((value: string) => {
    return value.replace(/[\s\-\(\)\+]/g, '');
  }, []);

  // Phone lookup effect
  useEffect(() => {
    const trimmedPhone = attendeeForm.phone.trim();
    const normalizedDigits = normalizePhoneNumber(trimmedPhone);

    if (!trimmedPhone || normalizedDigits.length < 6) {
      setPhoneLookupResults([]);
      setPhoneLookupLoading(false);
      setPhoneLookupError(null);
      setHasAttemptedPhoneLookup(false);
      return;
    }

    let isCancelled = false;
    setHasAttemptedPhoneLookup(true);
    setPhoneLookupLoading(true);
    setPhoneLookupError(null);

    const debounceId = setTimeout(async () => {
      try {
        const data = await openScheduleService.getAllAttendees(agencySlug, { search: trimmedPhone });
        if (isCancelled) {
          return;
        }
        
        // Filter results to match normalized phone numbers
        const filteredMatches = data.attendees.filter(attendee => {
          const attendeeNormalized = normalizePhoneNumber(attendee.phone || '');
          return attendeeNormalized.includes(normalizedDigits);
        }).map(attendee => ({
          name: attendee.name,
          email: attendee.email, // Explicitly preserve email field
          phone: attendee.phone
        }));
        
        console.log('Filtered matches:', filteredMatches); // Debug log
        setPhoneLookupResults(filteredMatches.slice(0, 5));
      } catch (error) {
        console.error('Error searching attendees by phone:', error);
        if (!isCancelled) {
          setPhoneLookupError('Unable to look up attendees right now. You can continue entering details manually.');
          setPhoneLookupResults([]);
        }
      } finally {
        if (!isCancelled) {
          setPhoneLookupLoading(false);
        }
      }
    }, 400);

    return () => {
      isCancelled = true;
      clearTimeout(debounceId);
    };
  }, [attendeeForm.phone, agencySlug, normalizePhoneNumber]);

  // Reset editing state when session changes (dialog opens with different session)
  useEffect(() => {
    if (session) {
      setEditing(false);
    }
  }, [session?._id]);

  // Reset attendee form and lookup when dialog closes
  useEffect(() => {
    if (!open) {
      setAttendeeForm({ name: '', email: '', phone: '' });
      setPhoneLookupResults([]);
      setPhoneLookupLoading(false);
      setPhoneLookupError(null);
      setHasAttemptedPhoneLookup(false);
    }
  }, [open]);

  const handleSelectExistingAttendee = (attendee: { name: string; email?: string; phone: string }) => {
    console.log('Selecting attendee:', attendee); // Debug log
    const newForm = {
      name: attendee.name || '',
      email: attendee.email || '',
      phone: attendee.phone || ''
    };
    console.log('Setting form to:', newForm); // Debug log
    setAttendeeForm(newForm);
    setPhoneLookupResults([]);
    setHasAttemptedPhoneLookup(false);
  };

  // Populate form with session data when entering edit mode
  useEffect(() => {
    if (session && editing) {
      // Compute original duration in minutes from session start/end times
      if (session.startTime && session.endTime) {
        const [startH, startM] = session.startTime.split(':').map(Number);
        const [endH, endM] = session.endTime.split(':').map(Number);
        if (!Number.isNaN(startH) && !Number.isNaN(startM) && !Number.isNaN(endH) && !Number.isNaN(endM)) {
          const startTotal = startH * 60 + startM;
          const endTotal = endH * 60 + endM;
          const duration = endTotal - startTotal;
          if (duration > 0) {
            setSessionDurationMinutes(duration);
          }
        }
      }

      setSessionForm({
        startTime: session.startTime || '',
        endTime: session.endTime || '',
        notes: session.notes || '',
        maxAttendees: session.maxAttendees?.toString() || '',
        status: session.status || 'scheduled' as const
      });
    }
  }, [session, editing]);

  const handleSessionUpdate = async () => {
    if (!session) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const updateData = {
        startTime: sessionForm.startTime,
        endTime: sessionForm.endTime,
        notes: sessionForm.notes,
        maxAttendees: sessionForm.maxAttendees ? parseInt(sessionForm.maxAttendees) : undefined,
        status: sessionForm.status
      };

      console.log('Session object before update:', session);
      console.log('Session ID (_id):', session._id);
      console.log('Session ID (id):', (session as any).id);
      console.log('All session properties:', Object.keys(session));
      
      const sessionId = session._id || (session as any).id;
      if (!sessionId) {
        throw new Error('Session ID is missing');
      }
      
      const updatedSession = await openScheduleService.updateSession(
        scheduleId,
        sessionId,
        updateData,
        agencySlug
      );
      
      onSessionUpdate(updatedSession);
      setEditing(false);
      setSuccess('Session updated successfully');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      // Prefer API-provided message if available (e.g., conflict hint from backend)
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      setError(apiMessage || 'Failed to update session');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAttendee = async () => {
    if (!session) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const updatedSession = await openScheduleService.registerAttendee(
        scheduleId,
        session._id,
        attendeeForm,
        agencySlug
      );
      
      onSessionUpdate(updatedSession);
      setAttendeeForm({ name: '', email: '', phone: '' });
      setSuccess('Attendee registered successfully');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to register attendee');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!session) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Session object before status update:', session);
      console.log('Session ID (_id) for status update:', session._id);
      console.log('Session ID (id) for status update:', (session as any).id);
      console.log('All session properties for status update:', Object.keys(session));
      
      const sessionId = session._id || (session as any).id;
      if (!sessionId) {
        throw new Error('Session ID is missing for status update');
      }
      
      const updatedSession = await openScheduleService.updateSession(
        scheduleId,
        sessionId,
        { status: newStatus },
        agencySlug
      );
      
      onSessionUpdate(updatedSession);
      setSessionForm(prev => ({ ...prev, status: newStatus as 'scheduled' | 'in_progress' | 'completed' | 'cancelled' }));
      setSuccess(`Session status updated to ${newStatus}`);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update session status');
    } finally {
      setLoading(false);
    }
  };

  const isAtCapacity = session && session.maxAttendees && session.currentAttendees >= session.maxAttendees;

  if (!session) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Session Details - Week {session.weekNumber}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Session Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Session Information
              </Typography>
              
              {editing ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Start Time"
                      type="time"
                      value={sessionForm.startTime}
                      onChange={(e) => {
                        const newStartTime = e.target.value;

                        // If we know the original duration, automatically adjust end time
                        if (sessionDurationMinutes && newStartTime) {
                          const [h, m] = newStartTime.split(':').map(Number);
                          if (!Number.isNaN(h) && !Number.isNaN(m)) {
                            const startTotal = h * 60 + m;
                            const endTotal = startTotal + sessionDurationMinutes;
                            const endH = Math.floor(endTotal / 60) % 24;
                            const endM = endTotal % 60;
                            const endTimeStr = `${endH.toString().padStart(2, '0')}:${endM
                              .toString()
                              .padStart(2, '0')}`;
                            setSessionForm(prev => ({
                              ...prev,
                              startTime: newStartTime,
                              endTime: endTimeStr
                            }));
                            return;
                          }
                        }

                        // Fallback: just update start time
                        setSessionForm(prev => ({
                          ...prev,
                          startTime: newStartTime
                        }));
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="End Time"
                      type="time"
                      value={sessionForm.endTime}
                      onChange={(e) => setSessionForm({ ...sessionForm, endTime: e.target.value })}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Max Attendees"
                      type="number"
                      value={sessionForm.maxAttendees}
                      onChange={(e) => setSessionForm({ ...sessionForm, maxAttendees: e.target.value })}
                      fullWidth
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                                             <Select
                         value={sessionForm.status}
                         onChange={(e) => setSessionForm({ ...sessionForm, status: e.target.value as 'scheduled' | 'in_progress' | 'completed' | 'cancelled' })}
                         label="Status"
                       >
                        <MenuItem value="scheduled">Scheduled</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Notes"
                      value={sessionForm.notes}
                      onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Date</Typography>
                    <Typography variant="body1">
                      {openScheduleService.formatDate(session.date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Time</Typography>
                    <Typography variant="body1">
                      {openScheduleService.formatTime(session.startTime)} - {openScheduleService.formatTime(session.endTime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip 
                      label={session.status.replace('_', ' ').toUpperCase()} 
                      size="small"
                      sx={{ 
                        bgcolor: openScheduleService.getSessionStatusColor(session.status),
                        color: 'white'
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Attendees</Typography>
                    <Typography variant="body1">
                      {session.currentAttendees}
                      {session.maxAttendees && ` / ${session.maxAttendees}`}
                    </Typography>
                  </Grid>
                  {session.notes && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Notes</Typography>
                      <Typography variant="body1">{session.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Paper>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={1}>
                {!editing ? (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditing(true)}
                    fullWidth
                  >
                    Edit Session
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleSessionUpdate}
                      disabled={loading}
                      fullWidth
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setEditing(false)}
                      fullWidth
                    >
                      Cancel
                    </Button>
                  </>
                )}
                
                <Divider sx={{ my: 1 }} />
                
                                 {session.status === 'scheduled' ? (
                   <Button
                     variant="outlined"
                     startIcon={<CheckCircleIcon />}
                     onClick={() => handleStatusChange('in_progress')}
                     disabled={loading}
                     fullWidth
                   >
                     Start Session
                   </Button>
                 ) : null}
                 
                 {session.status === 'in_progress' ? (
                   <Button
                     variant="outlined"
                     startIcon={<CheckCircleIcon />}
                     onClick={() => handleStatusChange('completed')}
                     disabled={loading}
                     fullWidth
                   >
                     Complete Session
                   </Button>
                 ) : null}
                 
                 {session.status !== 'completed' && session.status !== 'cancelled' ? (
                   <Button
                     variant="outlined"
                     color="error"
                     startIcon={<CancelIcon />}
                     onClick={() => handleStatusChange('cancelled')}
                     disabled={loading}
                     fullWidth
                   >
                     Cancel Session
                   </Button>
                 ) : null}
              </Box>
            </Paper>
          </Grid>

          {/* Attendee Registration */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Attendee Registration
              </Typography>
              
              {isAtCapacity && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This session is at maximum capacity
                </Alert>
              )}
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Phone"
                    value={attendeeForm.phone}
                    onChange={(e) => setAttendeeForm({ ...attendeeForm, phone: e.target.value })}
                    fullWidth
                    disabled={isAtCapacity}
                    helperText="Start with a phone number to check for returning attendees"
                  />
                  
                  {hasAttemptedPhoneLookup && (
                    <Box sx={{ mt: 1 }}>
                      {phoneLookupLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} />
                          <Typography variant="caption" color="text.secondary">
                            Searching attendees...
                          </Typography>
                        </Box>
                      ) : phoneLookupResults.length > 0 ? (
                        <Paper sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                          <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 600 }}>
                            Select an existing attendee
                          </Typography>
                          <List dense>
                            {phoneLookupResults.map((attendee, index) => (
                              <ListItem key={index} disablePadding>
                                <ListItemButton onClick={() => handleSelectExistingAttendee(attendee)}>
                                  <ListItemText
                                    primary={attendee.name}
                                    secondary={
                                      <>
                                        {attendee.email && <Typography component="span" variant="body2" display="block">{attendee.email}</Typography>}
                                        <Typography component="span" variant="body2">{attendee.phone}</Typography>
                                      </>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          No existing attendees found for this phone number.
                        </Typography>
                      )}
                      
                      {phoneLookupError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                          {phoneLookupError}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Name"
                    value={attendeeForm.name}
                    onChange={(e) => setAttendeeForm({ ...attendeeForm, name: e.target.value })}
                    fullWidth
                    disabled={isAtCapacity}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Email"
                    type="email"
                    value={attendeeForm.email}
                    onChange={(e) => setAttendeeForm({ ...attendeeForm, email: e.target.value })}
                    fullWidth
                    disabled={isAtCapacity}
                  />
                </Grid>
              </Grid>
              
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleRegisterAttendee}
                disabled={loading || isAtCapacity || !attendeeForm.name || !attendeeForm.email}
                fullWidth
              >
                Register Attendee
              </Button>
            </Paper>
          </Grid>

          {/* Attendees List */}
          {session.attendees && session.attendees.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Registered Attendees ({session.attendees.length})
                </Typography>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Registered</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {session.attendees.map((attendee, index) => (
                        <TableRow key={index}>
                          <TableCell>{attendee.name}</TableCell>
                          <TableCell>{attendee.email}</TableCell>
                          <TableCell>{attendee.phone || '-'}</TableCell>
                          <TableCell>
                            {new Date(attendee.registeredAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionManager; 